import { Container, Sprite, type Texture } from "pixi.js";
import type { PresentationTaskRunner } from "@slot/presentation";
import type { CoinJackpotTier } from "../rules/symbols";
import { MYSTERY_REEL_TUNING } from "../tuning/featureTuning";
import type { Point } from "./flightPath";
import { FX_SCOPE } from "./fxScope";
import { MYSTERY_BOARD_SCALE, MYSTERY_REEL_LAYOUT } from "./layout";

/** A band of the reel: the red MYSTERY band the symbol shows on the board, or a jackpot's. */
type Band = "mystery" | CoinJackpotTier;

/** The designer's mystery reel pieces (`mystery-reel/`, the deferred bundle), each its whole canvas. */
export interface MysteryReelArt {
  readonly frame: Texture;
  readonly shade: Texture;
  readonly mask: Texture;
  readonly glow: Texture;
  readonly text: Texture;
  readonly bands: Readonly<Record<Band, Texture>>;
  readonly winFrame: Texture;
  readonly winShine: Texture;
  readonly sparkle: Texture;
}

/** The jackpot bands as they follow one another down the strip, top to bottom, round and round. */
const CYCLE: readonly CoinJackpotTier[] = ["major", "mini", "minor"];

/** Where the sparkles twinkle round the landed band, from the window's centre (frame pixels), and when (a share of the win). */
const SPARKLES: readonly { x: number; y: number; at: number }[] = [
  { x: -430, y: -150, at: 0.05 },
  { x: 410, y: 140, at: 0.2 },
  { x: 250, y: -175, at: 0.35 },
  { x: -260, y: 170, at: 0.5 },
  { x: 0, y: -190, at: 0.62 },
];

/** Its pieces, built once its art is in. */
interface Parts {
  readonly art: MysteryReelArt;
  readonly glow: Sprite;
  readonly strip: Container;
  readonly mask: Sprite;
  readonly shade: Sprite;
  readonly frame: Sprite;
  readonly text: Sprite;
  readonly winFrame: Sprite;
  readonly winShine: Sprite;
  readonly sparkles: readonly Sprite[];
}

/**
 * A mystery jackpot's reveal (the designer's mystery reel, 2026-10-12): its symbol leaves its cell for the board's
 * middle, growing, its "MYSTERY JACKPOT" fading and a golden glow rising behind it; its reel turns, the red MYSTERY band
 * sliding down out of its window and the jackpot bands after it (MAJOR, MINI, MINOR, round and round), and slows to a stop
 * on the jackpot the server revealed, a touch past it and back; the band lights (a gold frame round it, a light sweeping
 * across it, sparkles), then the symbol shrinks back into its cell, where the jackpot coin shows. Built from the
 * designer's pieces in their frame's pixels, the window's centre its origin, the bands masked by the window; its motion
 * the round's (its scope: a slam lands each step at once). Nothing plays before its pieces are in.
 */
export class MysteryReel extends Container {
  private readonly runner: PresentationTaskRunner;
  private parts: Parts | null = null;
  private spins = 0;
  private readonly landed: CoinJackpotTier[] = [];

  public constructor(input: { runner: PresentationTaskRunner }) {
    super({ label: "MysteryReel" });
    this.runner = input.runner;
    this.eventMode = "none";
    this.visible = false;
  }

  /** The designer's pieces, loaded. */
  public setArt(art: MysteryReelArt): void {
    if (this.parts || this.destroyed) return;
    const { window, frame, text, glow, mask, band, sparkle } = MYSTERY_REEL_LAYOUT;
    const sprite = (texture: Texture, width: number, height: number, label: string, anchor = 0.5): Sprite => {
      const one = new Sprite({ texture, anchor, label });
      one.setSize(width, height);
      return one;
    };
    const glowSprite = sprite(art.glow, glow, glow, "Glow");
    const strip = new Container({ label: "Strip" });
    const maskSprite = sprite(art.mask, mask, mask, "Window");
    strip.mask = maskSprite;
    const shade = sprite(art.shade, mask, mask, "Shade");
    const frameSprite = sprite(art.frame, frame.width, frame.height, "Frame", 0);
    frameSprite.position.set(-window.x, -window.y);
    const textSprite = sprite(art.text, text.width, text.height, "Text");
    const winFrame = sprite(art.winFrame, band.width, band.height, "WinFrame", 0);
    const winShine = sprite(art.winShine, band.width, band.height, "WinShine", 0);
    const sparkles = SPARKLES.map((spot) => {
      const one = sprite(art.sparkle, sparkle, sparkle, "Sparkle");
      one.position.set(spot.x, spot.y);
      return one;
    });
    this.addChild(glowSprite, strip, maskSprite, shade, frameSprite, ...sparkles, textSprite);
    this.parts = { art, glow: glowSprite, strip, mask: maskSprite, shade, frame: frameSprite, text: textSprite, winFrame, winShine, sparkles };
  }

  /** Whether its pieces are in. */
  public get ready(): boolean {
    return this.parts !== null;
  }

  /** Whether its pieces are in, how many reveals it has spun, and the jackpots it landed on (the dev hook's read). */
  public get state(): { loaded: boolean; spins: number; landed: CoinJackpotTier[] } {
    return { loaded: this.parts !== null, spins: this.spins, landed: [...this.landed] };
  }

  /**
   * The mystery jackpot on `from` (this layer's space: its cell's centre) reveals `tier`: up to `to` (the board's middle),
   * grown, its reel spinning to the jackpot, lit there, then back to `from`. It resolves once it is back and gone (the
   * cell shows the jackpot coin from then); at once before its pieces are in.
   */
  public async play(input: { from: Point; to: Point; tier: CoinJackpotTier }): Promise<void> {
    const parts = this.parts;
    if (!parts || this.destroyed) return;
    this.spins += 1;
    const T = MYSTERY_REEL_TUNING;
    const { strip, glow, text, winFrame, winShine, sparkles } = parts;
    const { start, end, result } = this.buildStrip(input.tier);
    const board = MYSTERY_BOARD_SCALE;
    const big = MYSTERY_BOARD_SCALE * T.grow;
    // As the board shows it: on its cell, at its size, "MYSTERY JACKPOT" over the red band.
    strip.y = -start;
    text.alpha = 1;
    glow.alpha = 0;
    for (const one of sparkles) one.scale.set(0);
    this.position.set(input.from.x, input.from.y);
    this.scale.set(board);
    this.visible = true;
    try {
      // Up to the board's middle, growing; its text fading, the glow rising.
      await this.step(T.riseSeconds, (k) => {
        const e = easeInOut(k);
        this.position.set(input.from.x + (input.to.x - input.from.x) * e, input.from.y + (input.to.y - input.from.y) * e);
        this.scale.set(board + (big - board) * e);
        text.alpha = Math.max(0, 1 - k / T.textFadeShare);
        glow.alpha = e;
      });
      // The reel spins down to the jackpot, slowing to it, a touch past it and back.
      await this.step(T.spinSeconds, (k) => {
        strip.y = -(start + (end - start) * backOut(k, T.overshoot));
      });
      strip.y = -end;
      this.landed.push(input.tier);
      // The band lights: its gold frame, a light sweeping across it, the sparkles.
      winFrame.position.set(result.x, result.y);
      winShine.position.set(result.x - MYSTERY_REEL_LAYOUT.band.width, result.y);
      strip.addChild(winFrame, winShine);
      await this.step(T.winSeconds, (k) => {
        winFrame.alpha = Math.min(1, k / T.winFrameInShare);
        winShine.x = result.x + MYSTERY_REEL_LAYOUT.band.width * (2 * Math.min(1, k / T.shineShare) - 1);
        for (const [i, one] of sparkles.entries()) {
          const t = (k - SPARKLES[i]!.at) / T.sparkleShare;
          const s = t > 0 && t < 1 ? Math.sin(Math.PI * t) : 0;
          one.scale.set((s * MYSTERY_REEL_LAYOUT.sparkle) / Math.max(1, one.texture.width));
          one.rotation = t * Math.PI;
        }
      });
      // Back into its cell, shrinking, the glow going.
      await this.step(T.returnSeconds, (k) => {
        const e = easeInOut(k);
        this.position.set(input.to.x + (input.from.x - input.to.x) * e, input.to.y + (input.from.y - input.to.y) * e);
        this.scale.set(big + (board - big) * e);
        glow.alpha = 1 - e;
      });
    } finally {
      if (!this.destroyed) {
        this.visible = false;
        winFrame.removeFromParent();
        winShine.removeFromParent();
      }
    }
  }

  /** One step of the reveal: `seconds` of the round's runner, in its scope (a slam lands it at once). */
  private async step(seconds: number, onUpdate: (k: number) => void): Promise<void> {
    await this.runner.tweenProgress(
      seconds,
      (k) => {
        if (!this.destroyed) onUpdate(k);
      },
      FX_SCOPE,
      { easing: "linear" },
    );
    if (!this.destroyed) onUpdate(1);
  }

  /**
   * The strip for `tier`, top to bottom: a jackpot band above it, it, the spin's bands (a jackpot band never twice in a
   * row), the MYSTERY band and one under it. Its bands' centres: `start` the MYSTERY band's (on the board, in the window),
   * `end` the jackpot's; `result` the jackpot band's top-left (where it lights).
   */
  private buildStrip(tier: CoinJackpotTier): { start: number; end: number; result: Point } {
    const parts = this.parts!;
    const others = CYCLE.filter((band) => band !== tier);
    const between: CoinJackpotTier[] = [];
    let previous: CoinJackpotTier = tier;
    for (let i = 0; between.length < MYSTERY_REEL_TUNING.spinBands; i++) {
      const band = CYCLE[i % CYCLE.length]!;
      if (band === previous) continue;
      between.push(band);
      previous = band;
    }
    const names: Band[] = [others[1]!, tier, ...between, "mystery", others[0]!];
    const { band } = MYSTERY_REEL_LAYOUT;
    for (const child of parts.strip.removeChildren()) if (child !== parts.winFrame && child !== parts.winShine) child.destroy();
    let y = 0;
    const centres: number[] = [];
    for (const name of names) {
      const height = name === "mystery" ? band.mysteryHeight : band.height;
      const sprite = new Sprite({ texture: parts.art.bands[name], label: `Band:${name}` });
      sprite.setSize(band.width, height);
      sprite.position.set(-band.width / 2, y);
      parts.strip.addChild(sprite);
      centres.push(y + height / 2);
      y += height;
    }
    const resultTop = centres[1]! - band.height / 2;
    return { start: centres[names.lastIndexOf("mystery")]!, end: centres[1]!, result: { x: -band.width / 2, y: resultTop } };
  }
}

/** Eased in and out (cubic). */
function easeInOut(k: number): number {
  return k < 0.5 ? 4 * k * k * k : 1 - (-2 * k + 2) ** 3 / 2;
}

/** Fast out of the start, slowing to the end, `overshoot` past it and back (the "back" ease). */
function backOut(k: number, overshoot: number): number {
  const u = k - 1;
  return 1 + (overshoot + 1) * u * u * u + overshoot * u * u;
}
